import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Select, Tag, type SelectProps } from 'antd'
import type { BusinessType } from '@super-mega/server/contracts/business'
import { BusinessEditorModal } from '@/features/master-data/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { mergeOptions } from '@/utils/select-options.ts'
import type { BusinessOption } from './service-order-form.types.ts'

export function QuickBusinessSelect({
  businessType,
  canCreate,
  options = [],
  onChange,
  disabled,
  className,
  ...props
}: Omit<SelectProps<number, BusinessOption>, 'onChange'> & {
  businessType: BusinessType
  canCreate: boolean
  onChange?: (value: number, option?: BusinessOption) => void
}) {
  const [creating, setCreating] = useState(false)
  const [createdOptions, setCreatedOptions] = useState<BusinessOption[]>([])
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mergedOptions = mergeOptions(createdOptions, options)
  const renderBusiness = (option: BusinessOption) => (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{option.label}</span>
      <Tag color={option.defaultServiceMode === 'term' ? 'cyan' : 'blue'} variant="outlined">
        {option.defaultServiceMode === 'term' ? '期限服务' : '一次性服务'}
      </Tag>
    </span>
  )

  return (
    <div className="flex min-w-0 items-start gap-2">
      <Select<number, BusinessOption>
        {...props}
        className={cn('min-w-0 flex-1', className)}
        disabled={disabled}
        options={mergedOptions}
        optionRender={businessType === 'valueAdded' ? (option) => renderBusiness(option.data) : undefined}
        labelRender={
          businessType === 'valueAdded'
            ? ({ value, label }) => {
                const selected = mergedOptions.find((option) => option.value === value)
                return selected ? renderBusiness(selected) : label
              }
            : undefined
        }
        onChange={(value, option) => onChange?.(value, Array.isArray(option) ? option[0] : option)}
      />
      {canCreate && (
        <Button disabled={disabled} onClick={() => setCreating(true)}>
          快速创建
        </Button>
      )}
      {creating && (
        <BusinessEditorModal
          editing="new"
          fixedType={businessType}
          onClose={() => setCreating(false)}
          onSaved={async (business) => {
            const option: BusinessOption = {
              defaultServiceMode: business.defaultServiceMode,
              value: business.id,
              label: business.name,
              referencePrice: business.referencePrice,
              waterUnitPrice: business.waterUnitPrice,
              electricityUnitPrice: business.electricityUnitPrice,
              depositAmount: business.depositAmount,
              managementFee: business.managementFee
            }
            setCreatedOptions((previous) => mergeOptions([option], previous))
            onChange?.(option.value, option)
            setCreating(false)
            await Promise.all([
              queryClient.invalidateQueries(trpc.business.options.queryFilter()),
              queryClient.invalidateQueries(trpc.business.list.queryFilter())
            ])
          }}
        />
      )}
    </div>
  )
}
